// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/derivadosDaGravacao.ts

/**
 * Os números que o motor derivou na gravação, como a linha da grade os recebe de volta. A tela
 * não conhece a fórmula: escreve o que voltou.
 *
 * **A regra que decide tudo aqui: chave AUSENTE preserva a célula; chave NULA apaga.** É por
 * chave, e não regra do corpo — na grade de fornecedores o `PMZNewCost` vem presente e nulo.
 */

import type { BaseDaMargemAlvo, MotivoSemSugestao, OrigemDaVerba } from './verbaDeSellIn';

export type DerivadosDaGravacao = {
  PMZNewCost?: number | null;
  effectiveNetCost?: number | null;
  newPriceMargin?: number | null;
  /** Não confundir com `newPriceCompetitors`, que é a competitividade e mora em outra coluna. */
  marginCompetitorsPrice?: number | null;
  /** A verba no custo de entrada. Numa linha sem acordo, a leitura devolve aqui a SUGERIDA. */
  sellinAllowanceToSupplier?: number | null;
};

/** **Um estado só**, aplicado em bloco: o motor manda ou a sugestão, ou o motivo de não haver. */
export type ContratoDaVerbaNaGravacao = {
  suggestedSellinAllowance?: number | null;
  sellinAllowanceMarginBasis?: BaseDaMargemAlvo | null;
  noSellinAllowanceSuggestionReason?: MotivoSemSugestao | null;
};

/** **Regra INVERSA das três acima:** vêm sempre, e vazio é "não há verba" — então é APLICADO. */
export type EstadoPersistidoDaVerbaNaGravacao = {
  sellinAllowance?: number | null;
  sellinAllowanceOrigin?: OrigemDaVerba | null;
};

export type PatchDaGravacao = DerivadosDaGravacao & ContratoDaVerbaNaGravacao & EstadoPersistidoDaVerbaNaGravacao;

const CHAVES_DERIVADAS: Array<keyof DerivadosDaGravacao> = [
  'PMZNewCost',
  'effectiveNetCost',
  'newPriceMargin',
  'marginCompetitorsPrice',
  'sellinAllowanceToSupplier',
];

const CHAVES_DO_CONTRATO_DA_VERBA: Array<keyof ContratoDaVerbaNaGravacao> = [
  'suggestedSellinAllowance',
  'sellinAllowanceMarginBasis',
  'noSellinAllowanceSuggestionReason',
];

const CHAVES_DO_ESTADO_PERSISTIDO: Array<keyof EstadoPersistidoDaVerbaNaGravacao> = ['sellinAllowance', 'sellinAllowanceOrigin'];

/** Decididas UMA A UMA; o trio da sugestão vai em bloco, abaixo. */
const CHAVES_DO_PATCH: Array<keyof PatchDaGravacao> = [...CHAVES_DERIVADAS, ...CHAVES_DO_ESTADO_PERSISTIDO];

const algumaVeio = (corpo: Record<string, unknown>, chaves: Array<string>): boolean =>
  chaves.some(chave => Object.prototype.hasOwnProperty.call(corpo, chave) && corpo[chave] !== undefined);

/** Devolve **apenas as chaves que vieram**: o resto da linha não é tocado. */
export const aplicarDerivadosDaGravacao = (resposta: unknown): PatchDaGravacao => {
  const patch: PatchDaGravacao = {};

  if (resposta === null || typeof resposta !== 'object') return patch;

  const corpo = resposta as Record<string, unknown>;
  const escrito = patch as Record<string, unknown>;

  // Veio qualquer uma, as três são escritas e a que faltou entra nula.
  if (algumaVeio(corpo, CHAVES_DO_CONTRATO_DA_VERBA)) {
    CHAVES_DO_CONTRATO_DA_VERBA.forEach(chave => {
      escrito[chave] = algumaVeio(corpo, [chave]) ? corpo[chave] : null;
    });
  }

  CHAVES_DO_PATCH.forEach(chave => {
    // As duas guardas servem às duas regras opostas do contrato, e nenhuma delas sobra:
    // presente-e-nulo APLICA (estado persistido), ausente PRESERVA (derivados).
    if (!Object.prototype.hasOwnProperty.call(corpo, chave)) return;
    if (corpo[chave] === undefined) return;

    escrito[chave] = corpo[chave];
  });

  return patch;
};

/**
 * **Linha com verba nao pode adiantar:** o calculo do navegador e cego a ela e sairia mais ALTO
 * que a verdade. A lista e a MESMA de {@link aplicarDerivadosDaGravacao}, e tem de continuar.
 */
export const numerosQueATelaPodeAdiantar = (temVerba: boolean, numeros: DerivadosDaGravacao): DerivadosDaGravacao => {
  if (temVerba) return {};

  const permitidos: DerivadosDaGravacao = {};

  CHAVES_DERIVADAS.forEach(chave => {
    if (!Object.prototype.hasOwnProperty.call(numeros, chave)) return;
    if (numeros[chave] === undefined) return;

    permitidos[chave] = numeros[chave];
  });

  return permitidos;
};
